import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SetupAccount } from './AccountConsole';
import { AcceptInvite } from './AcceptInvite';
import { isReturningV1Customer } from '../../lib/account/legacyLogin';
import { readInvitation } from '../../lib/account/invitation';

/**
 * App-wide provisioning gate. A just-authenticated user — especially a JIT-migrated v1 user — holds a
 * valid v2 token that carries NO tenancy claims (custom:accountId / custom:role) until they've been
 * provisioned via POST /accounts and the pre-token-gen trigger re-stamps the token. Every withAuth
 * endpoint 403s until then.
 *
 * So we block the entire authenticated shell (nav + routes) behind this gate: if the token has no
 * custom:accountId, show ONLY the SetupAccount flow. Provisioning force-refreshes the session (so the
 * new claims land) and invalidates the claims query, after which the app renders normally. This makes
 * it impossible to reach a withAuth page — via landing route or a nav click — without tenancy claims.
 */
export function RequireAccount({
  onSignOut,
  children,
}: {
  onSignOut: () => void;
  children: React.ReactNode;
}) {
  const qc = useQueryClient();

  const claims = useQuery({
    queryKey: ['claims'],
    queryFn: async () => {
      const s = await fetchAuthSession();
      return (s.tokens?.idToken?.payload ?? {}) as Record<string, unknown>;
    },
  });
  const hasAccount = Boolean(claims.data?.['custom:accountId']);
  /*
   * Whether this is an EXISTING customer arriving from v1, which decides how the first-run screen reads.
   * The claim is on the token the sign-in already produced, so this costs nothing extra.
   */
  const returning = isReturningV1Customer(claims.data);

  if (claims.isLoading || (!hasAccount && !claims.isError)) {
    // While claims load, and briefly after provisioning before the refreshed token arrives.
    if (!claims.isSuccess) {
      return <div className="loading-screen">// loading your account…</div>;
    }
  }

  /*
   * An invitee has no account BY DESIGN — joining one is the whole point — so the provisioning gate must
   * stand aside for them. Without this they were shown the first-run flow and asked to name an
   * organisation and enter its addresses, which would have created a SECOND account rather than joining
   * the one that invited them.
   *
   * Read from the URL rather than from state: this is the first render after sign-in, and the token is in
   * the link they followed.
   */
  const onInviteLink = typeof window !== 'undefined'
    && readInvitation(window.location.pathname, window.location.search) !== null;

  if (claims.isSuccess && !hasAccount && onInviteLink) {
    return (
      <div className="auth-page grain" style={{ minHeight: '100vh' }}>
        <div className="auth-page-bg" />
        <AcceptInvite
          onDone={async () => {
            /*
             * REFETCH, not invalidate: this has to finish before we return. The accept page clears the
             * token from the URL as it completes, so if the new claims were merely queued this gate would
             * re-render with neither an account nor an invite link and drop a fresh member into the
             * create-an-account form.
             */
            try {
              await fetchAuthSession({ forceRefresh: true });
              await qc.refetchQueries({ queryKey: ['claims'] });
            } catch {
              // They ARE in the account; only the token refresh failed. Reloading re-bootstraps the
              // session from Cognito, which is a better answer than the provisioning form.
              window.location.assign('/app');
            }
          }}
        />
      </div>
    );
  }

  if (claims.isSuccess && !hasAccount) {
    return (
      <div className="auth-page grain" style={{ minHeight: '100vh' }}>
        <div className="auth-page-bg" />
        <SetupAccount
          returning={returning}
          onDone={async () => {
            /*
             * REFETCH, not invalidate: this has to finish before we return. The accept page clears the
             * token from the URL as it completes, so if the new claims were merely queued this gate would
             * re-render with neither an account nor an invite link and drop a fresh member into the
             * create-an-account form.
             */
            try {
              await fetchAuthSession({ forceRefresh: true });
              await qc.refetchQueries({ queryKey: ['claims'] });
            } catch {
              // They ARE in the account; only the token refresh failed. Reloading re-bootstraps the
              // session from Cognito, which is a better answer than the provisioning form.
              window.location.assign('/app');
            }
          }}
        />
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onSignOut}
            style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--text-dim)' }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
